// Capa de datos del panel — AHORA INDEPENDIENTE.
// Reimplementa las mismas funciones que antes hablaban con Firestore, pero
// contra NUESTRA API REST (backend Fastify). Sin Firebase, sin svabogadas.
//
// Mapeo de modelo: el backend guarda columnas (codigo, cliente, tema, estado,
// abogadaId, captadoraId) + un JSON `data` con el resto de campos ricos del
// caso (rut, nombres, audiencias, checklist, documentos, pagos, notas...).
// Aquí traducimos entre ese modelo y la forma que espera la UI del panel.
import { api, API_URL, getToken, TENANT } from './api';
import { perfilActual } from './auth';

/* ── Helpers de formato (puros, sin cambios) ──────────── */
const pad2 = (value: number) => String(value).padStart(2, '0');
const formatDateValue = (date: Date) => `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;
export const fmtDate = (value: any) => {
  if (value == null || value === '') return '—';
  if (value instanceof Date && !isNaN(value.getTime())) return formatDateValue(value);
  if (typeof value === 'string') {
    // Fecha sin hora: se muestra tal cual. Pasarla por new Date() la leería
    // como UTC y en Chile aparecería el día anterior, lo que en el borde del
    // cierre de mes hace dudar de si un pago entró o no en el período.
    const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (soloFecha) return `${soloFecha[3]}-${soloFecha[2]}-${soloFecha[1]}`;
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const iso = new Date(value);
      if (!isNaN(iso.getTime())) return formatDateValue(iso);
      return value.slice(0, 10).split('-').reverse().join('-');
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value.replace(/\//g, '-');
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return formatDateValue(parsed);
    return value;
  }
  return '—';
};
export const fmtMoney = (n: number) => n ? '$' + Number(n).toLocaleString('es-CL') : '—';

/** Lee un monto escrito a mano, con o sin separadores de miles.
 *
 *  En Chile el punto separa los miles: "250.000" son doscientos cincuenta mil.
 *  Un <input type="number"> lee ese mismo punto como coma decimal, así que el
 *  navegador entregaba 250 y eso era lo que se guardaba — un caso quedó con
 *  cuotas de $250 en vez de $250.000 y nadie lo notó hasta cuadrar la caja.
 *  Acá valen las dos grafías (y la coma, que es lo que sale del teclado
 *  numérico del teléfono): se queda solo con los dígitos. Los pesos no llevan
 *  centavos, así que no hay decimales que perder. */
export const parseMoney = (v: string | number | null | undefined): number => {
  if (typeof v === 'number') return v;
  const digitos = String(v ?? '').replace(/[^0-9]/g, '');
  return digitos ? Number(digitos) : 0;
};

/** Lo mismo que escribe la persona, pero con los miles separados a medida que
 *  teclea. Que el campo muestre "250.000" mientras se escribe es lo que evita
 *  el error de entrada: la magnitud queda a la vista antes de guardar. */
export const fmtMiles = (v: string | number | null | undefined): string => {
  const digitos = String(v ?? '').replace(/[^0-9]/g, '');
  return digitos ? Number(digitos).toLocaleString('es-CL') : '';
};

/** Cantidad de UF escrita a mano: "3,5" o "3.5". Si trae coma, los puntos son
 *  de miles ("1.234,5"); sin coma, el punto es el decimal. */
export const parseUf = (v: string | number | null | undefined): number => {
  if (typeof v === 'number') return v > 0 ? v : 0;
  const s = String(v ?? '').replace(/\s/g, '');
  if (!s) return 0;
  const n = s.includes(',') ? Number(s.replace(/\./g, '').replace(',', '.')) : Number(s);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 10000) / 10000 : 0;
};
/** 3.5 → "3,50 UF". */
export const fmtUf = (n: number) => Number(n).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + ' UF';
/** Valor de la UF en pesos, con sus centavos: "$41.242,32". */
export const fmtValorUf = (n: number) => '$' + Number(n).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** UF del día según el SII; la guarda y la sirve el backend (lib/uf.ts). */
export async function getUf() {
  return api<{ fecha: string; valor: number; fuente: string }>('/uf');
}

export const statusColors: Record<string, string> = {
  'En evaluación': '#7A6A8A',
  'Pausa': '#5A6A7A',
  'Aceptado': '#5C7A5E',
  'En tramitación': '#B8965A',
  'Resuelto / Cerrado': '#4A5E4A',
  'Desistido': '#8A5050',
  'Inviable': '#6A4A4A',
  'Contacto': '#4A6A8A',
  'Solo Consulta': '#4A8A7A',
};

/* ── Mapeo backend ↔ panel ────────────────────────────── */
type CasoBackend = {
  id: string; codigo: string; cliente: string; tema: string | null;
  estado: string | null; abogadaId: string | null; captadoraId: string | null;
  data: Record<string, any> | null; creado: string; actualizado: string;
};

// Backend → forma rica que consume la UI (las columnas mandan sobre el JSON).
function mapCaso(c: CasoBackend) {
  return {
    ...(c.data || {}),
    id: c.id,
    codigoCaso: c.codigo,
    codigoPublico: (c as any).codigoPublico || null,
    // Lo manda el servidor cuando el correo del cliente rebota o fue marcado
    // como spam: sin esto el estudio no se entera de que sus avisos no llegan.
    correoRebotado: (c as any).correoRebotado || null,
    nombreCliente: c.cliente,
    tema: c.tema,
    estado: c.estado,
    abogadaId: c.abogadaId,
    captadoraId: c.captadoraId,
    createdAt: c.creado,
    updatedAt: c.actualizado,
  };
}

const undef = (v: any) => (v === '' || v == null ? undefined : v);

async function getRaw(id: string): Promise<CasoBackend> {
  return api<CasoBackend>(`/casos/${id}`);
}
async function patchCaso(id: string, body: Record<string, any>) {
  return api<CasoBackend>(`/casos/${id}`, { method: 'PATCH', body });
}
// Lee el caso, muta su JSON `data` y lo guarda (para arrays embebidos).
async function patchData(id: string, mutator: (data: Record<string, any>) => void) {
  const raw = await getRaw(id);
  const data = { ...(raw.data || {}) };
  mutator(data);
  await patchCaso(id, { data });
}

/* ── Usuario ──────────────────────────────────────────── */
export async function getUserData(_uid: string) {
  const p = perfilActual();
  return p
    ? { rol: p.rol, nombre: p.nombre, activo: p.activo, estudio: p.estudio, emailVerificado: p.emailVerificado, modulos: p.modulos ?? [] }
    : { rol: 'captadora', nombre: '', estudio: '', emailVerificado: true };
}

/* ── Casos ────────────────────────────────────────────── */
export async function loadCasos(_rol: string, _uid: string) {
  // El backend ya filtra por tenant + rol según el token.
  const casos = await api<CasoBackend[]>('/casos');
  return casos.map(mapCaso);
}

export async function getCaso(id: string) {
  try {
    const caso: any = mapCaso(await getRaw(id));
    try { caso.documentos = await loadDocumentos(id); } catch { caso.documentos = caso.documentos || []; }
    try { caso.carpetas = await loadCarpetas(id); } catch { caso.carpetas = []; }
    return caso;
  } catch {
    return null;
  }
}

const COLUMNAS: Record<string, string> = {
  nombreCliente: 'cliente', tema: 'tema', estado: 'estado',
  abogadaId: 'abogadaId', captadoraId: 'captadoraId',
};

export async function updateCampo(id: string, field: string, value: any) {
  if (COLUMNAS[field]) await patchCaso(id, { [COLUMNAS[field]]: value });
  else await patchData(id, (data) => { data[field] = value; });
}
/** Varios campos de `data` en UN solo guardado. Dos updateCampo en paralelo
 *  no sirven: cada uno lee el caso, cambia lo suyo y reescribe `data` entera,
 *  así que el segundo pisa al primero con la copia vieja. Así una etapa del
 *  proceso borrada volvía a aparecer. */
export async function updateCamposData(id: string, campos: Record<string, any>) {
  await patchData(id, (data) => { Object.assign(data, campos); });
}

export async function updateEstado(id: string, estado: string, prevEstado?: string, hasFechaAceptado?: boolean) {
  const raw = await getRaw(id);
  const data = { ...(raw.data || {}) };
  if (estado === 'Aceptado' && prevEstado !== 'Aceptado') {
    const now = new Date().toISOString();
    if (!hasFechaAceptado) data.fechaAceptado = now;
    data.fechaUltimoAceptado = now;
  }
  await patchCaso(id, { estado, data });
}

/* ── Audiencias / Checklist / Documentos / Pagos (JSON embebido) ── */
export async function addAudiencia(id: string, aud: any) {
  await patchData(id, (d) => { d.audiencias = [...(d.audiencias || []), aud]; });
}
export async function removeAudiencia(id: string, index: number) {
  await patchData(id, (d) => { (d.audiencias || []).splice(index, 1); });
}
export async function updateAudiencia(id: string, index: number, patch: Record<string, any>) {
  await patchData(id, (d) => { if (d.audiencias?.[index]) d.audiencias[index] = { ...d.audiencias[index], ...patch }; });
}
/** Cambia el estado de una audiencia (por su id) y, si viene, agenda la nueva
 *  fecha como otra audiencia enlazada. Va en UN guardado para que el servidor
 *  anote las dos cosas juntas en el historial. */
export async function cambiarAudiencia(casoId: string, audienciaId: string, cambios: Record<string, any>, nueva?: Record<string, any>) {
  await patchData(casoId, (d) => {
    const lista: any[] = d.audiencias || [];
    const i = lista.findIndex((a) => a?.id === audienciaId);
    if (i < 0) throw new Error('Esa audiencia ya no está en el caso. Recarga la página e inténtalo de nuevo.');
    lista[i] = { ...lista[i], ...cambios, ...(nueva ? { reagendadaId: nueva.id } : {}) };
    if (nueva) lista.push(nueva);
    d.audiencias = lista;
  });
}

/** Da por cumplido (o vuelve a dejar pendiente) un plazo legal.
 *
 *  Los plazos no son filas de la base: los calcula la ley a partir de la fecha
 *  de despido y de las audiencias del caso. Lo único que hay que guardar es
 *  cuál se dio por cumplido, y eso vive en el propio caso para que lo vea todo
 *  el estudio y no solo quien lo marcó. La llave incluye el vencimiento, así
 *  que si la fecha de base se corrige el plazo reaparece sin marcar. */
export async function marcarPlazoRealizado(casoId: string, clave: string, done: boolean, por: string) {
  await patchData(casoId, (d) => {
    const marcados = { ...(d.plazosRealizados || {}) };
    if (done) marcados[clave] = { fecha: new Date().toISOString().slice(0, 10), por };
    else delete marcados[clave];
    d.plazosRealizados = marcados;
  });
}

/** Identificador estable de una acción del checklist. Es lo que vincula la
 *  acción con sus notificaciones: sin él había que comparar textos, y dos
 *  acciones con la misma redacción se tachaban juntas. */
export const nuevoIdTarea = () => 't' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

export async function addCheckItem(id: string, texto: string, tareaId?: string) {
  const item: any = { texto, done: false, id: tareaId || nuevoIdTarea() };
  await patchData(id, (d) => { d.checklist = [...(d.checklist || []), item]; });
  return item.id as string;
}
export async function toggleCheckItem(id: string, index: number, done: boolean) {
  await patchData(id, (d) => { if (d.checklist?.[index]) d.checklist[index].done = done; });
}
export async function removeCheckItem(id: string, index: number) {
  await patchData(id, (d) => { (d.checklist || []).splice(index, 1); });
}
export async function updateCheckItem(id: string, index: number, texto: string) {
  await patchData(id, (d) => { if (d.checklist?.[index]) d.checklist[index] = { ...d.checklist[index], texto }; });
}

// ── Documentos (Amazon S3 vía backend) ────────────────────────────────
export async function loadDocumentos(casoId: string) {
  return api<any[]>(`/casos/${casoId}/documentos`);
}
// Sube un archivo/blob a un caso: pide URL firmada al backend y hace PUT a S3.
export async function subirDocumento(casoId: string, file: Blob, nombre: string, tipo?: string, carpetaId?: string | null) {
  const contentType = (file as File).type || 'application/octet-stream';
  // El servidor crea la fila primero porque necesita darnos el enlace firmado.
  // Si la subida falla, hay que deshacerla: si no, queda un documento visible
  // en el caso que no se puede abrir nunca.
  const res = await api<{ documento: any; uploadUrl: string }>(`/casos/${casoId}/documentos`, {
    method: 'POST',
    body: { nombre, tipo, contentType, size: file.size, carpetaId: carpetaId || null },
  });
  let put: Response;
  try {
    put = await fetch(res.uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
  } catch (e: any) {
    await api(`/documentos/${res.documento.id}`, { method: 'DELETE' }).catch(() => undefined);
    // Un fallo de red acá suele ser la conexión o un bloqueo del navegador.
    throw new Error(`No se pudo enviar el archivo a destino (${e?.message || 'error de red'}).`);
  }
  if (!put.ok) {
    const detalle = await put.text().catch(() => '');
    await api(`/documentos/${res.documento.id}`, { method: 'DELETE' }).catch(() => undefined);
    // El detalle de S3 viene en XML; se extrae el código para poder diagnosticar.
    const codigo = /<Code>([^<]+)<\/Code>/.exec(detalle)?.[1];
    throw new Error(`El almacenamiento rechazó el archivo (${put.status}${codigo ? ' · ' + codigo : ''}).`);
  }
  // Solo ahora el documento pasa a ser real: el servidor comprueba en S3.
  await api(`/documentos/${res.documento.id}/confirmar`, { method: 'POST' });
  return res.documento;
}
// URL temporal para ver/descargar un documento. Con `descargar`, el enlace
// baja el archivo aunque sea un PDF o una foto que el navegador mostraría.
export async function documentoUrl(docId: string, descargar = false): Promise<string> {
  const r = await api<{ url: string }>(`/documentos/${docId}/url${descargar ? '?descargar=1' : ''}`);
  return r.url;
}
// Todos los documentos de un caso en un .zip, con sus carpetas. Lo arma el
// servidor (ver GET /casos/:id/documentos/zip); acá se baja con el token y se
// guarda. Con `carpetaId`, solo los de esa carpeta.
export async function descargarDocumentosCaso(casoId: string, carpetaId?: string): Promise<void> {
  const q = carpetaId ? `?carpetaId=${encodeURIComponent(carpetaId)}` : '';
  const res = await fetch(`${API_URL}/casos/${casoId}/documentos/zip${q}`, {
    headers: { Authorization: `Bearer ${getToken() || ''}` },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => null);
    throw new Error(e?.error || 'No se pudieron descargar los documentos.');
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)/) || cd.match(/filename="([^"]+)"/);
  const nombre = m ? decodeURIComponent(m[1]) : 'documentos.zip';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
// Vista previa de un Word (.docx): el servidor lo convierte a HTML. El panel
// tiene que sanearlo con DOMPurify antes de pintarlo.
export async function documentoVista(docId: string): Promise<{ nombre: string; html: string }> {
  return api<{ nombre: string; html: string }>(`/documentos/${docId}/vista`);
}
export async function eliminarDocumento(docId: string) {
  await api(`/documentos/${docId}`, { method: 'DELETE' });
}
// Cambiar el nombre visible de un documento. El archivo en S3 no se toca; el
// servidor le devuelve la extensión original si el nombre nuevo la perdió.
export async function renombrarDocumento(docId: string, nombre: string) {
  return api<{ id: string; nombre: string }>(`/documentos/${docId}`, {
    method: 'PATCH',
    body: { nombre },
  });
}

// Carpetas del caso: opcionales, un solo nivel. Borrar una deja sus
// documentos sin carpeta.
export async function loadCarpetas(casoId: string) {
  return api<{ id: string; nombre: string }[]>(`/casos/${casoId}/carpetas`);
}
export async function crearCarpeta(casoId: string, nombre: string) {
  return api<{ id: string; nombre: string }>(`/casos/${casoId}/carpetas`, { method: 'POST', body: { nombre } });
}
export async function renombrarCarpeta(carpetaId: string, nombre: string) {
  return api<{ id: string; nombre: string }>(`/carpetas/${carpetaId}`, { method: 'PATCH', body: { nombre } });
}
export async function eliminarCarpeta(carpetaId: string) {
  await api(`/carpetas/${carpetaId}`, { method: 'DELETE' });
}
// carpetaId null = sacarlos de su carpeta.
export async function moverDocumentos(casoId: string, ids: string[], carpetaId: string | null) {
  return api<{ movidos: number }>(`/casos/${casoId}/documentos/mover`, { method: 'POST', body: { ids, carpetaId } });
}

export async function addPago(id: string, pago: any) {
  await patchData(id, (d) => { d.pagos = [...(d.pagos || []), pago]; });
}
export async function removePago(id: string, index: number) {
  await patchData(id, (d) => { (d.pagos || []).splice(index, 1); });
}

/* ── Crear / eliminar caso ────────────────────────────── */
export async function crearCaso(data: any) {
  const caso = await api<CasoBackend>('/casos', {
    method: 'POST',
    body: {
      cliente: data.nombreCliente || data.cliente || 'Sin nombre',
      tema: undef(data.tema),
      estado: undef(data.estado) || 'Contacto',
      abogadaId: undef(data.abogadaId),
      captadoraId: undef(data.captadoraId),
      data,
    },
  });
  return { codigo: caso.codigo, id: caso.id };
}

// Eliminar = borrado suave (va a la papelera; se puede restaurar).
export async function deleteCaso(id: string) {
  await api(`/casos/${id}`, { method: 'DELETE' });
}
/* ── Papelera (borrado suave de casos) ─────────────────────────────────── */
export async function loadPapelera() {
  const casos = await api<CasoBackend[]>('/casos/papelera');
  return casos.map(mapCaso);
}
export async function restaurarCaso(id: string) {
  await api(`/casos/${id}/restaurar`, { method: 'POST' });
}
export async function eliminarCasoDefinitivo(id: string) {
  await api(`/casos/${id}/definitivo`, { method: 'DELETE' });
}

/* ── Usuarios ─────────────────────────────────────────── */
type UsuarioBackend = { id: string; email: string; nombre: string; rol: string; activo: boolean; creado: string };
const mapUsuario = (u: UsuarioBackend) => ({ ...u, uid: u.id, rol: u.rol.toLowerCase() });

export async function loadUsuarios() {
  const us = await api<UsuarioBackend[]>('/usuarios');
  return us.map(mapUsuario);
}
export async function loadAbogadas() {
  const us = await api<UsuarioBackend[]>('/usuarios');
  // El admin va incluido a propósito: en un estudio chico quien administra es
  // también quien litiga, y dejarlo fuera hacía imposible que el fundador se
  // asignara sus propios casos — que es justo lo que hace el primer día.
  //
  // El admin/no abogado NO va, y esa es toda su razón de existir: administra el
  // estudio pero no lleva causas, así que no puede aparecer en este selector ni
  // en el reparto de honorarios, que se calcula sobre esta misma lista. El
  // servidor rechaza igual la asignación; esto solo evita ofrecerla.
  return us
    .map(mapUsuario)
    .filter(
      (u) =>
        u.rol === 'admin' ||
        u.rol === 'abogada' ||
        u.rol === 'abogado_asociado' ||
        u.rol === 'abogado_externo',
    );
}

/* ── Gestión de usuarios (admin) ──────────────────────────────────────── */
// El panel usa roles en minúscula; el backend los espera en MAYÚSCULA.
export async function crearUsuario(data: { nombre: string; email: string; password?: string; rol: string }) {
  return api('/usuarios', { method: 'POST', body: { ...data, rol: (data.rol || '').toUpperCase() } });
}
export async function actualizarUsuario(id: string, cambios: { nombre?: string; rol?: string; activo?: boolean; password?: string }) {
  const body: any = { ...cambios };
  if (cambios.rol) body.rol = cambios.rol.toUpperCase();
  return api(`/usuarios/${id}`, { method: 'PATCH', body });
}
export async function desactivarUsuario(id: string) {
  return actualizarUsuario(id, { activo: false });
}
/** Lo que tiene un usuario antes de eliminarlo, y si se puede. */
export type ResumenEliminar = {
  nombre: string; ingresos: number; ultimaActividad: string | null; edicionesDeCasos: number;
  casosEditados: { codigo: string; cliente: string }[]; casosACargo: { codigo: string; cliente: string }[];
  diasDeAsistencia: number; avisos: number; empleado: string | null; conversacionesChatbularis: number;
  puedeEliminar: boolean; motivo: string | null;
};
export const antesDeEliminarUsuario = (id: string) => api<ResumenEliminar>(`/usuarios/${id}/antes-de-eliminar`);
/** Elimina un usuario desactivado y sin casos a su nombre. */
export async function eliminarUsuario(id: string) {
  await api(`/usuarios/${id}`, { method: 'DELETE' });
}
export async function reactivarUsuario(id: string) {
  return actualizarUsuario(id, { activo: true });
}
// Envía el enlace de recuperación de contraseña al correo del usuario (usa el tenant actual).
export async function enviarRecuperacion(email: string) {
  return api('/auth/forgot', { method: 'POST', auth: false, body: { subdominio: TENANT, email } });
}
export async function loadCaptadoras() {
  const us = await api<UsuarioBackend[]>('/usuarios');
  // Todo el equipo, sin mirar el rol. Captar no es un cargo: un abogado, una
  // procuradora o el propio admin traen clientes igual que un captador, y
  // filtrar por rol='captadora' dejaba fuera a quien de verdad había captado
  // el caso —obligando a anotarlo mal o a dejarlo sin captador.
  return us.map(mapUsuario);
}

export async function cambiarAbogada(casoId: string, abogadaId: string, abogadaNombre: string) {
  const raw = await getRaw(casoId);
  await patchCaso(casoId, { abogadaId: undef(abogadaId), data: { ...(raw.data || {}), abogada: abogadaNombre } });
}
export async function cambiarCaptadora(casoId: string, captadoraId: string, captadoraNombre: string) {
  const raw = await getRaw(casoId);
  await patchCaso(casoId, { captadoraId: undef(captadoraId), data: { ...(raw.data || {}), captadora: captadoraNombre } });
}

/* ── Redactor con IA (evaluaciones) ───────────────────── */
export async function redactarEvaluacion(notas: string, tema?: string): Promise<string> {
  const r = await api<{ texto: string }>('/ia/evaluacion', { method: 'POST', body: { notas, tema } });
  return r.texto;
}

/* ── Ajustes del estudio (bono de captadoras, etc.) ───── */
export async function loadAjustes() {
  return api<any>('/ajustes');
}
export async function guardarAjustes(cambios: any) {
  return api<any>('/ajustes', { method: 'PATCH', body: cambios });
}

/* ── Plantillas de proceso por disciplina ─────────────── */
export async function loadPlantillas() {
  return api<any[]>('/plantillas');
}
export async function crearPlantilla(nombre: string, pasos: string[]) {
  return api<any>('/plantillas', { method: 'POST', body: { nombre, pasos } });
}
export type ImpactoPlantilla = {
  casos: { id: string; codigo: string; cliente: string; etapasPerdidas: string[]; marcadasAntes: number }[];
  totalCasos: number;
  casosConPerdida: number;
  etapasPerdidasTotal: number;
};

/** Qué casos usan esta disciplina y qué avance perderían con estas etapas.
 *  No modifica nada: alimenta el aviso previo a guardar. */
export async function impactoPlantilla(id: string, pasos: string[]) {
  return api<ImpactoPlantilla>(`/plantillas/${id}/impacto`, { method: 'POST', body: { pasos } });
}

export async function editarPlantilla(
  id: string,
  cambios: { nombre?: string; pasos?: string[]; orden?: number; activa?: boolean; propagar?: boolean },
) {
  return api<any>(`/plantillas/${id}`, { method: 'PATCH', body: cambios });
}
export async function eliminarPlantilla(id: string) {
  await api(`/plantillas/${id}`, { method: 'DELETE' });
}

/* ── Avisos al cliente ────────────────────────────────────
   Los manda el servidor, con el nombre del estudio a la vista y respondiendo
   a su casilla. Antes colgaban de una Cloud Function que dejó de existir en
   la migración: el botón decía "Enviando…" y no llegaba nada. */
export async function avisarCliente(casoId: string, tipo: 'bienvenida' | 'estado') {
  return api<{ ok: boolean; enviadoA: string }>(`/casos/${casoId}/avisar-cliente`, { method: 'POST', body: { tipo } });
}

/* ── Reportes ─────────────────────────────────────────── */
export async function loadReportesData() {
  const casos = await api<CasoBackend[]>('/casos');
  return casos.map(mapCaso);
}

// Configuración del flujo de caja: gastos y reparto. Vive en el SERVIDOR,
// dentro de los ajustes del estudio.
//
// Antes estaba en localStorage bajo una única clave 'cfg_flujoCaja', sin
// distinguir el estudio. Eso significaba tres cosas malas: se perdía al
// cambiar de computador, cada miembro del equipo veía cifras distintas, y dos
// estudios abiertos en el mismo navegador se pisaban la configuración.
export async function getFlujoCajaConfig() {
  try {
    const a = await api<any>('/ajustes');
    return { ...(a?.flujoCaja || {}), reparto: a?.reparto, periodo: a?.periodo };
  } catch { return null; }
}
export async function saveFlujoCajaConfig(config: any) {
  const { reparto, periodo: _periodo, ...flujoCaja } = config || {};
  const cuerpo: any = {};
  if (reparto) cuerpo.reparto = reparto;
  // Solo se manda flujoCaja si trae algo. Enviarlo vacío al guardar únicamente
  // el reparto haría que el servidor reemplazara los gastos del estudio por un
  // objeto vacío: se perderían todos los gastos cargados.
  if (Object.keys(flujoCaja).length > 0) cuerpo.flujoCaja = flujoCaja;
  if (Object.keys(cuerpo).length === 0) return null;
  return api<any>('/ajustes', { method: 'PATCH', body: cuerpo });
}

/* ── Cuadre de caja de fin de mes ──
   Uno por mes contable ("AAAA-MM"). Tabla CuadreCaja en el servidor; quién y
   cuándo los pone el servidor. */
export type CuadreCaja = { periodo: string; cuadrado: boolean; nota: string | null; totalCobrado: number | null; usuarioNombre: string | null; actualizado: string };
/** Marca (o desmarca) un pago como visto en la cartola del banco. Se busca
 *  por posición y se confirma por concepto y fecha de pago: si alguien cambió
 *  las cuotas del caso mientras tanto, no se marca otro pago por error. */
export type EnBanco = { por: string; el: string };
export async function marcarPagoEnBanco(casoId: string, indice: number, esperado: { concepto?: string; fechaPago?: string }, marcado: boolean): Promise<EnBanco | null> {
  const raw = await getRaw(casoId);
  const data = { ...(raw.data || {}) };
  const pagos = Array.isArray(data.pagos) ? [...data.pagos] : [];
  const pg = pagos[indice];
  if (!pg || (pg.concepto || '') !== (esperado.concepto || '') || (pg.fechaPago || '') !== (esperado.fechaPago || '')) {
    throw new Error('Los pagos de este caso cambiaron. Recarga Reportes e inténtalo de nuevo.');
  }
  const { enBanco: _previo, ...resto } = pg;
  const enBanco = marcado ? { por: perfilActual()?.nombre || '', el: new Date().toISOString() } : null;
  pagos[indice] = enBanco ? { ...resto, enBanco } : resto;
  await patchCaso(casoId, { data: { ...data, pagos } });
  return enBanco;
}
export const clavePeriodo =(anio: number, mes: number) => `${anio}-${String(mes + 1).padStart(2, '0')}`;
export async function getCuadresCaja(): Promise<CuadreCaja[]> {
  return api<CuadreCaja[]>('/cuadres-caja');
}
export async function guardarCuadreCaja(periodo: string, cuerpo: { cuadrado: boolean; nota?: string; totalCobrado?: number }): Promise<CuadreCaja> {
  return api<CuadreCaja>(`/cuadres-caja/${periodo}`, { method: 'PUT', body: cuerpo });
}

/* ── Notificaciones (menciones @usuario / @todos y @plazo del checklist) ──
   Backend REST propio (modelo Notificacion, aislado por tenant + por usuario).
   El panel espera "tiempo real": lo resolvemos con sondeo cada 30s. */
export async function crearNotificacion(data: any) {
  try {
    await api('/notificaciones', {
      method: 'POST',
      body: {
        para: data.para,
        casoId: data.casoId,
        mensaje: data.mensaje,
        plazo: data.plazo,
        deNombre: data.deNombre,
        casoNombre: data.casoNombre,
        codigoCaso: data.codigoCaso,
        // Sin esto el aviso quedaba sin vínculo con su acción del checklist y
        // había que encontrarla por el texto, que se rompe al editarla.
        tareaId: data.tareaId,
      },
    });
  } catch (e) { console.error('crearNotificacion:', e); }
}
/** Las acciones del checklist que el usuario le asignó a otras personas, una
 *  por acción, con sus destinatarios y si cada uno ya vio el aviso. */
export async function loadDelegadas() {
  return api<any[]>('/notificaciones/delegadas');
}
// Marca la acción del checklist como realizada (✓) en las notificaciones de
// todos sus destinatarios (mismo caso + mismo texto).
export async function actualizarRealizadaPorTexto(casoId: string, texto: string, done: boolean, tareaId?: string) {
  try { await api('/notificaciones/realizado', { method: 'POST', body: { casoId, texto, tareaId, done } }); }
  catch (e) { console.error('actualizarRealizadaPorTexto:', e); }
}
export async function actualizarNotificacionesMensaje(_casoId: string, _mv: string, _mn: string, _plazo?: string) {
  /* el texto de las notificaciones ya emitidas se conserva; nada que hacer */
}
export async function eliminarNotificacion(notifId: string) {
  try { await api(`/notificaciones/${notifId}`, { method: 'DELETE' }); } catch (e) { console.error(e); }
}
export async function marcarRealizada(_notifId: string, casoId: string, texto: string, done: boolean, tareaId?: string) {
  await actualizarRealizadaPorTexto(casoId, texto, done, tareaId);
}
export async function limpiarNotificacionesViejas(_uid?: string) {
  try { await api('/notificaciones/limpiar', { method: 'POST' }); } catch { /* silencioso */ }
}
export function subscribeNotificaciones(_uid: string, cb: (notifs: any[]) => void) {
  let vivo = true;
  const cargar = async () => {
    try { const ns = await api<any[]>('/notificaciones'); if (vivo) cb(ns); }
    catch { if (vivo) cb([]); }
  };
  cargar();
  const t = setInterval(cargar, 30000);
  return () => { vivo = false; clearInterval(t); };
}
export async function marcarNotificacionLeida(notifId: string) {
  try { await api(`/notificaciones/${notifId}/leer`, { method: 'PATCH' }); } catch (e) { console.error(e); }
}
export async function marcarTodasLeidas(_uid?: string) {
  try { await api('/notificaciones/leer-todas', { method: 'POST' }); } catch (e) { console.error(e); }
}

/* ── Calendario: enlace de suscripción (.ics) para Google/Apple/Outlook ── */
export async function calendarioFeedUrl(): Promise<{ url: string; https: string }> {
  return api<{ url: string; https: string }>('/calendario/feed');
}

/* ── Respaldo exportable (.zip con Excel + documentos) — solo admin ──────
   Descarga binaria: fetch con token, arma un blob y dispara la descarga. */
/* ── Versión desplegada ───────────────────────────────── */
/** Qué versión del servidor corre y desde cuándo. Sin token: /health es público. */
export async function versionServidor(): Promise<{ version: string; construido: string | null; arriba_desde: string }> {
  return api('/health', { auth: false });
}

export async function exportarDatos(): Promise<void> {
  const res = await fetch(`${API_URL}/export`, {
    headers: { Authorization: `Bearer ${getToken() || ''}` },
  });
  if (!res.ok) {
    const msg = res.status === 403 ? 'Solo el administrador puede exportar los datos.' : 'No se pudo generar el respaldo.';
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const nombre = m ? m[1] : `tabularis-respaldo-${new Date().toISOString().slice(0, 10)}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/* ── Asistencia de captadores ─────────────────────────────────────────
   Estas tres eran cascarones vacíos desde la migración: la pantalla marcaba
   el día, no lo guardaba en ninguna parte y al salir se perdía. Ahora van al
   servidor (tabla Asistencia, aislada por estudio). */
export async function loadAsistenciaMes(mes: number, anio: number): Promise<any[]> {
  // El panel cuenta los meses desde 0 (como Date); el servidor, desde 1.
  return api<any[]>(`/asistencia?mes=${mes + 1}&anio=${anio}`);
}
/** Marca un día (1 = completo, 0.5 = medio). Los nombres que manda la
 *  pantalla no se guardan: el servidor anota quién marcó a partir del token. */
export async function marcarAsistencia(captadoraId: string, _captadoraNombre: string, fecha: string, _porId: string, _porNombre: string, valor: number) {
  await api('/asistencia', { method: 'PUT', body: { captadoraId, fecha, valor } });
}
export async function desmarcarAsistencia(captadoraId: string, fecha: string) {
  await api(`/asistencia/${encodeURIComponent(captadoraId)}/${fecha}`, { method: 'DELETE' });
}

/* ── Foro de abogados ──────────────────────────────────────────────────
   Leer es público y escribir es solo para abogados con el estudio al día,
   pero desde el panel siempre hay sesión, así que todo pasa por `api`. El
   servidor decide: acá no se replica ninguna regla de permisos. */
export type ForoCategoria = { slug: string; nombre: string; descripcion?: string };
export type ForoFirma = { autor: string; anonimo: boolean };
export type ForoTema = ForoFirma & {
  slug: string; titulo: string; respuestas: number;
  creado: string; ultimaActividad: string;
  categoria: { slug: string; nombre: string };
};
export type ForoMensaje = ForoFirma & { id: string; cuerpo: string; creado: string; editado?: string };

export async function foroCategorias(): Promise<ForoCategoria[]> {
  const r = await api<{ categorias: ForoCategoria[] }>('/foro/categorias');
  return r.categorias || [];
}
export async function foroTemas(categoria?: string, pagina = 1) {
  const q = new URLSearchParams();
  if (categoria) q.set('categoria', categoria);
  if (pagina > 1) q.set('pagina', String(pagina));
  const s = q.toString();
  return api<{ temas: ForoTema[]; total: number; pagina: number; paginas: number }>(`/foro/temas${s ? '?' + s : ''}`);
}
export async function foroTema(slug: string) {
  return api<{ tema: ForoTema & { id: string; cuerpo: string }; respuestas: ForoMensaje[] }>(
    `/foro/temas/${encodeURIComponent(slug)}`,
  );
}
/** ¿Este usuario puede publicar? Se pregunta al servidor en vez de mirar el rol
 *  acá: la regla es una sola y vive allá. */
export async function foroPermiso(): Promise<boolean> {
  try {
    const r = await api<{ puedePublicar: boolean }>('/foro/permiso');
    return !!r.puedePublicar;
  } catch { return false; }
}
export async function foroCrearTema(data: { titulo: string; cuerpo: string; categoria: string; anonimo: boolean }) {
  return api<{ slug: string }>('/foro/temas', { method: 'POST', body: data });
}
export async function foroResponder(temaId: string, data: { cuerpo: string; anonimo: boolean }) {
  return api<{ id: string }>(`/foro/temas/${encodeURIComponent(temaId)}/respuestas`, { method: 'POST', body: data });
}
export async function foroDenunciar(data: { temaId?: string; respuestaId?: string; motivo: string }) {
  return api<{ ok: true }>('/foro/denuncias', { method: 'POST', body: data });
}
